/**
 * Pure helper functions shared by the GHG and Statistics page cluster.
 * No hooks, no JSX — safe to import from any component or test.
 */

import { format } from 'date-fns';
import { formatNumber } from '../../utils/numberFormat';
import type { DailyChartData } from '../../components/statistics/StatisticsChart';

// ---------------------------------------------------------------------------
// GHG – transport modes
// ---------------------------------------------------------------------------

export interface TransportMode {
  key: string;
  label: string;
  color: string;
}

export const TRANSPORT_MODES: TransportMode[] = [
  { key: 'road',  label: 'ROAD',  color: '#1976d2' },
  { key: 'air',   label: 'AIR',   color: '#2e7d32' },
  { key: 'sea',   label: 'SEA',   color: '#0288d1' },
  { key: 'rail',  label: 'RAIL',  color: '#ed6c02' },
  { key: 'ferry', label: 'FERRY', color: '#9c27b0' },
  { key: 'iww',   label: 'IWW',   color: '#00796b' },
  { key: 'hub',   label: 'HUB',   color: '#616161' },
];

export interface TransportModeChartEntry {
  name: string;
  TONS: number;
  EI: number;
  /** Per-bar colour, read directly by recharts as each datum's `fill`. */
  fill: string;
}

/**
 * Convert a `transport_chain_elements` object (from either CompanyGHG or the
 * GHGOverview aggregated block) into the array consumed by the bar charts.
 */
export function buildTransportModeChartData(
  transportChainElements: Record<string, { tot_emissions: number; ei: number }> | undefined | null,
): TransportModeChartEntry[] {
  if (!transportChainElements) return [];
  return TRANSPORT_MODES.map((mode) => {
    const modeData = transportChainElements[mode.key];
    return {
      name: mode.label,
      TONS: modeData ? modeData.tot_emissions / 1000 : 0,
      EI: modeData ? modeData.ei : 0,
      fill: mode.color,
    };
  });
}

// ---------------------------------------------------------------------------
// GHG – formatting
// ---------------------------------------------------------------------------

/** Convert kg → tonnes and format to 2 dp. */
export function formatEmissions(value: number): string {
  return formatNumber(value / 1000, 2);
}

// ---------------------------------------------------------------------------
// Shared – date-range filter → API options object
// ---------------------------------------------------------------------------

export interface DateRangeOptions {
  fromDate?: string;
  toDate?: string;
  year?: number;
}

/**
 * Build the date-filter portion of an API options object.
 * Returns `{ fromDate, toDate }` in dateRange mode or `{ year }` in year mode.
 */
export function buildDateRangeOptions(
  filterMode: 'year' | 'dateRange',
  fromDate: Date | null,
  toDate: Date | null,
  selectedYear: number,
): DateRangeOptions {
  if (filterMode === 'dateRange' && fromDate && toDate) {
    return {
      fromDate: format(fromDate, 'yyyy-MM-dd'),
      toDate: format(toDate, 'yyyy-MM-dd'),
    };
  }
  return { year: selectedYear };
}

// ---------------------------------------------------------------------------
// Statistics – daily chart data transformer
// ---------------------------------------------------------------------------

/**
 * Map a raw daily-stats array (from either CompanyStatistics or StatisticsOverview)
 * into the DailyChartData shape consumed by StatisticsChart.
 */
export function buildStatsDailyChartData(
  daily: Array<{
    day: string;
    year: number;
    month: number;
    total_transactions: number | string;
    total_geolocations: number | string;
    total_unique_shipments: number | string;
  }> | undefined | null,
): DailyChartData[] {
  if (!daily) return [];
  return daily.map((d) => ({
    day: d.day,
    year: d.year,
    month: d.month,
    transactions: Number(d.total_transactions) || 0,
    geolocations: Number(d.total_geolocations) || 0,
    uniqueShipments: Number(d.total_unique_shipments) || 0,
  }));
}
